import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Check, Sparkles, Plus, Lock, 
  Cpu, Rocket, Target, Github, Linkedin, AlertCircle, Loader,
  Camera, Trash2
} from 'lucide-react';
import API_URL from '../../config';
import AutocompleteInput from './AutocompleteInput';
import '../../pages/events/Events.css';
import '../../pages/events/EventsPrototype.css';

const DEPARTMENTS = ['CSE', 'CSE-AIML&IOT', 'AIDS', 'CSBS', 'IT', 'ECE', 'EEE', 'EIE', 'MECH', 'CIVIL', 'BIOTECH', 'DS', 'CYS', 'R&AI'];
const YEARS = ['1st year', '2nd year', '3rd year', '4th year'];
const SECTIONS = ['A', 'B', 'C', 'D', 'IOT', 'None'];

const TECH_INTERESTS_LIST = [
  'Artificial Intelligence', 'Machine Learning', 'Computer Vision', 'Generative AI',
  'Web Development', 'Mobile Apps', 'Cloud Computing', 'Cybersecurity & CTF',
  'Data Science', 'Blockchain & Web3', 'IoT & Embedded', 'Robotics & Automation',
  'Competitive Programming', 'System Design', 'DevOps', 'UI/UX Design', 'Startups'
];

const SUGGESTED_SKILLS = [
  'Python', 'JavaScript', 'TypeScript', 'React', 'Node.js', 'Next.js',
  'C++', 'Java', 'Flutter', 'TensorFlow', 'PyTorch', 'FastAPI',
  'PostgreSQL', 'MongoDB', 'Docker', 'AWS', 'Figma', 'Git', 'Linux'
];

const HOBBY_OPTIONS = [
  'Gaming', 'Photography', 'Music', 'Chess', 'Football', 'Cricket',
  'Badminton', 'Fitness & Gym', 'Reading', 'Travel', 'Anime', 'Cooking',
  'Podcasts', 'Film & Cinema', 'Volunteering'
];

const NETWORKING_INTENT_OPTIONS = [
  'Hackathon Teammates',
  'Project Collaborators',
  'Technical Networking',
  'Startup-minded People',
  'People with Similar Interests',
  'Faculty / Senior Mentors',
  'Alumni Connections',
  'Future Co-founders'
];

export default function EventProfileOnboardingModal({ 
  eventSlug, 
  eventName, 
  user, 
  activities, 
  onComplete, 
  onClose 
}) {
  const [step, setStep] = useState(1); // 1, 2, 3
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [skillInput, setSkillInput] = useState('');
  const [lookingForInput, setLookingForInput] = useState('');

  // Profile Photo State
  const [profilePic, setProfilePic] = useState(() => {
    if (user?.profile_pic) return user.profile_pic;
    try {
      const stored = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
      return stored.profile_pic || '';
    } catch {
      return '';
    }
  });
  const [isPhotoBusy, setIsPhotoBusy] = useState(false);
  const fileInputRef = useRef(null);

  // Form State - Pre-filled for internal VJLINK students
  const [name, setName] = useState(user?.name || '');
  const [rollNo, setRollNo] = useState(user?.rollNo || '');
  const [department, setDepartment] = useState(user?.department || 'CSE');
  const [year, setYear] = useState(user?.year || '3rd year');
  const [section, setSection] = useState(user?.section || 'A');
  const [contact, setContact] = useState(user?.contact || '');
  const [schoolName, setSchoolName] = useState((user?.school_name || '').toUpperCase());
  const [juniorCollege, setJuniorCollege] = useState((user?.junior_college || '').toUpperCase());
  const [juniorCollegeLocation, setJuniorCollegeLocation] = useState((user?.junior_college_location || '').toUpperCase());
  const [locality, setLocality] = useState((user?.current_locality || '').toUpperCase());

  const [skills, setSkills] = useState(() => {
    try { return user?.skills ? JSON.parse(user.skills) : []; }
    catch { return user?.skills ? [user.skills] : []; }
  });
  const [techInterests, setTechInterests] = useState(() => {
    try { return user?.interestsTechnical ? JSON.parse(user.interestsTechnical) : []; }
    catch { return []; }
  });
  const [hobbies, setHobbies] = useState(() => {
    try { return user?.hobbies ? JSON.parse(user.hobbies) : []; }
    catch { return user?.hobbies ? user.hobbies.split(',').map(h => h.trim()) : []; }
  });
  const [networkingIntent, setNetworkingIntent] = useState([]);
  const [lookingForSkills, setLookingForSkills] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [bio, setBio] = useState(user?.bio || '');
  const [github, setGithub] = useState(user?.github || '');
  const [linkedin, setLinkedin] = useState(user?.linkedin || '');

  const [localActivities, setLocalActivities] = useState(activities || []);

  // Photo handlers: updates users table and localStorage immediately so it reflects in main profile
  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please choose a valid image file (PNG, JPG, WebP).');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('Please choose an image below 2 MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      setProfilePic(dataUrl);
      setError(null);

      const stored = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
      const activeEmail = user?.email || stored.email;
      if (activeEmail) {
        setIsPhotoBusy(true);
        try {
          await fetch(`${API_URL}/upload_profile_pic`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: activeEmail, imageData: dataUrl })
          });
          const updated = { ...stored, profile_pic: dataUrl };
          localStorage.setItem('vjconnect_user', JSON.stringify(updated));
        } catch (err) {
          console.error('Failed to sync profile picture to main profile:', err);
        } finally {
          setIsPhotoBusy(false);
        }
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleRemovePhoto = async () => {
    setProfilePic('');
    const stored = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
    const activeEmail = user?.email || stored.email;
    if (activeEmail) {
      setIsPhotoBusy(true);
      try {
        await fetch(`${API_URL}/remove_profile_pic`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: activeEmail })
        });
        const updated = { ...stored, profile_pic: '' };
        localStorage.setItem('vjconnect_user', JSON.stringify(updated));
      } catch (err) {
        console.error('Failed to remove profile picture:', err);
      } finally {
        setIsPhotoBusy(false);
      }
    }
  };

  // Fetch activities if not passed
  useEffect(() => {
    if (activities && activities.length > 0) {
      setLocalActivities(activities);
      return;
    }
    if (!eventSlug) return;
    fetch(`${API_URL}/events/${eventSlug}`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.event && data.event.activities) {
          setLocalActivities(data.event.activities);
        }
      })
      .catch(() => {});
  }, [activities, eventSlug]);

  // Fetch current saved profile on mount so existing inputs are fully preserved
  useEffect(() => {
    if (!user?.email || !eventSlug) return;
    fetch(`${API_URL}/events-networking/${eventSlug}/profile/me?email=${user.email}`)
      .then(res => res.json())
      .then(data => {
        if (data && data.success) {
          const u = data.user || {};
          const p = data.profile || {};
          if (u.profile_pic) setProfilePic(u.profile_pic);
          else if (p.profile_pic) setProfilePic(p.profile_pic);
          if (u.name) setName(u.name);
          if (u.rollNo) setRollNo(u.rollNo);
          if (u.department) setDepartment(u.department);
          if (u.year) setYear(u.year);
          if (u.section) setSection(u.section);
          if (u.contact) setContact(u.contact);
          if (p.school_name || u.school_name) setSchoolName((p.school_name || u.school_name).toUpperCase());
          if (p.junior_college || u.junior_college) setJuniorCollege((p.junior_college || u.junior_college).toUpperCase());
          if (p.junior_college_location || u.junior_college_location) setJuniorCollegeLocation((p.junior_college_location || u.junior_college_location).toUpperCase());
          if (p.current_locality || u.current_locality) setLocality((p.current_locality || u.current_locality).toUpperCase());
          if (p.event_bio || p.bio || u.bio) setBio(p.event_bio || p.bio || u.bio);
          if (u.github) setGithub(u.github);
          if (u.linkedin) setLinkedin(u.linkedin);

          if (p.networking_intent) {
            try { setNetworkingIntent(JSON.parse(p.networking_intent)); } catch {}
          }
          if (p.looking_for_skills) {
            try { setLookingForSkills(JSON.parse(p.looking_for_skills)); } catch {}
          }
          if (p.skills) {
            try { 
              const parsedSkills = JSON.parse(p.skills);
              if (Array.isArray(parsedSkills) && parsedSkills.length > 0) setSkills(parsedSkills);
            } catch {}
          }
          if (p.interestsTechnical) {
            try {
              const parsedTech = JSON.parse(p.interestsTechnical);
              if (Array.isArray(parsedTech) && parsedTech.length > 0) setTechInterests(parsedTech);
            } catch {}
          }
          if (p.hobbies) {
            try {
              const parsedHobbies = JSON.parse(p.hobbies);
              if (Array.isArray(parsedHobbies) && parsedHobbies.length > 0) setHobbies(parsedHobbies);
            } catch {
              if (typeof p.hobbies === 'string') setHobbies(p.hobbies.split(',').map(h => h.trim()).filter(Boolean));
            }
          }
          if (data.activity_ids) {
            setSelectedActivities(data.activity_ids);
          }
        }
      })
      .catch(() => {});
  }, [user?.email, eventSlug]);

  const toggleItem = (list, setList, item) => {
    if (list.includes(item)) {
      setList(list.filter(x => x !== item));
    } else {
      setList([...list, item]);
    }
  };

  const handleAddSkill = (val) => {
    const trimmed = (val || skillInput).trim();
    if (trimmed && !skills.map(s => s.toLowerCase()).includes(trimmed.toLowerCase())) {
      setSkills([...skills, trimmed]);
    }
    setSkillInput('');
  };

  const handleRemoveSkill = (skillToRemove) => {
    setSkills(skills.filter(s => s !== skillToRemove));
  };

  const handleAddLookingForSkill = (val) => {
    const trimmed = (val || lookingForInput).trim();
    if (trimmed && !lookingForSkills.map(s => s.toLowerCase()).includes(trimmed.toLowerCase())) {
      setLookingForSkills([...lookingForSkills, trimmed]);
    }
    setLookingForInput('');
  };

  const handleRemoveLookingForSkill = (skillToRemove) => {
    setLookingForSkills(lookingForSkills.filter(s => s !== skillToRemove));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const stored = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
      const activeEmail = user?.email || stored.email;
      if (!activeEmail) {
        setError('Active user session not found. Please log in first.');
        setSaving(false);
        return;
      }

      const payload = {
        user_email: activeEmail,
        email: activeEmail,
        profile_pic: profilePic,
        name: name.trim() || user?.name || activeEmail.split('@')[0],
        rollNo: rollNo.trim(),
        department,
        year,
        section,
        contact: contact.trim(),
        school_name: schoolName.trim(),
        junior_college: juniorCollege.trim(),
        junior_college_location: juniorCollegeLocation.trim(),
        current_locality: locality.trim(),
        skills: JSON.stringify(skills),
        interestsTechnical: JSON.stringify(techInterests),
        interestsNonTechnical: Array.isArray(hobbies) ? hobbies.join(', ') : hobbies,
        aspirations: Array.isArray(hobbies) ? hobbies.join(', ') : hobbies,
        hobbies: Array.isArray(hobbies) ? hobbies.join(', ') : hobbies,
        bio: bio.trim(),
        github: github.trim(),
        linkedin: linkedin.trim(),
        networking_intent: networkingIntent,
        looking_for_skills: lookingForSkills,
        activity_ids: selectedActivities,
      };

      const res = await fetch(`${API_URL}/events-networking/${eventSlug}/profile/save-onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Failed to save profile. Please check the inputs.');
        setSaving(false);
        return;
      }

      const updatedUser = {
        ...stored,
        ...(data.user || {}),
        profile_pic: profilePic || data.user?.profile_pic || stored.profile_pic,
        name: name.trim() || user?.name,
        rollNo: rollNo.trim(),
        department,
        year,
        section,
        contact: contact.trim(),
        school_name: schoolName.trim(),
        junior_college: juniorCollege.trim(),
        skills,
        bio: bio.trim(),
        github: github.trim(),
        linkedin: linkedin.trim(),
        profile_completed: true,
        exists: true
      };

      localStorage.setItem('vjconnect_user', JSON.stringify(updatedUser));
      localStorage.setItem(`vjlink_profile_completed_${eventSlug}_${activeEmail}`, 'true');

      if (onComplete) onComplete(updatedUser);
    } catch (err) {
      console.error(err);
      setError('A network error occurred while saving your profile.');
    } finally {
      setSaving(false);
    }
  };

  const titles = [
    'Let people get to know you.',
    'What are you excited about?',
    'Who do you want to meet?'
  ];

  const copies = [
    "This isn't a résumé. Think of it as a quick introduction to the people you haven't met yet.",
    "Your skills show what you bring to the table. Your interests help you find peers you'll enjoy building with.",
    "Choose what you want out of this event. We'll use it to make recommendations more relevant."
  ];

  return (
    <div className="vp-modal-backdrop open" onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}>
      <div className="vp-modal" role="dialog" aria-modal="true" aria-labelledby="wizard-title">
        {/* Modal Head */}
        <div className="vp-modal-head">
          <div>
            <div className="vp-eyebrow">Your networking identity · Step {step} of 3</div>
            <h2 id="wizard-title">{titles[step - 1]}</h2>
          </div>
          <button className="vp-icon-btn" onClick={onClose} aria-label="Close modal">
            <X size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="vp-modal-body">
          {/* Stepper Indicator */}
          <div className="vp-stepper">
            <span className={step >= 1 ? 'done' : ''} />
            <span className={step >= 2 ? 'done' : ''} />
            <span className={step >= 3 ? 'done' : ''} />
          </div>

          <p className="vp-step-copy">{copies[step - 1]}</p>

          {error && (
            <div style={{
              background: '#fff0ee',
              border: '1px solid #ffd2cd',
              borderRadius: '12px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              color: '#a53b31',
              fontSize: '13px',
              marginBottom: '18px'
            }}>
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* STEP 1: Personal & Campus Identity */}
          {step === 1 && (
            <div className="vp-form-grid">
              {/* Profile Photo Uploader */}
              <div className="vp-field full" style={{
                background: '#f9f9f6',
                border: '1px solid #e8e8e2',
                borderRadius: '16px',
                padding: '16px',
                marginBottom: '4px'
              }}>
                <label style={{ fontSize: '13px', fontWeight: 700, color: '#171a24', marginBottom: '8px', display: 'block' }}>
                  Profile Photo
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      width: '74px',
                      height: '74px',
                      borderRadius: '50%',
                      background: profilePic ? '#eef2ff' : '#0f3d2e',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '26px',
                      fontWeight: 800,
                      cursor: 'pointer',
                      overflow: 'hidden',
                      position: 'relative',
                      border: '2px solid #0f3d2e',
                      flexShrink: 0,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}
                    title="Click to choose a photo"
                  >
                    {profilePic ? (
                      <img src={profilePic} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span>{(name || user?.name || '?').charAt(0).toUpperCase()}</span>
                    )}
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'rgba(0,0,0,0.5)',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Camera size={12} color="#fff" />
                    </div>
                  </div>

                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <input 
                      ref={fileInputRef} 
                      type="file" 
                      accept="image/*" 
                      style={{ display: 'none' }} 
                      onChange={handlePhotoSelect} 
                    />
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <button 
                        type="button" 
                        className="vp-btn vp-btn-lime vp-btn-small" 
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isPhotoBusy}
                      >
                        <Camera size={14} /> {profilePic ? 'Change photo' : 'Upload photo'}
                      </button>
                      {profilePic && (
                        <button 
                          type="button" 
                          className="vp-btn vp-btn-outline vp-btn-small" 
                          onClick={handleRemovePhoto}
                          disabled={isPhotoBusy}
                          style={{ color: '#a53b31', borderColor: '#ffd2cd' }}
                        >
                          <Trash2 size={13} /> Remove
                        </button>
                      )}
                      {isPhotoBusy && <Loader size={14} className="vp-spin" />}
                    </div>
                    <p style={{ fontSize: '11px', color: '#6b7280', margin: '6px 0 0', lineHeight: 1.4 }}>
                      Displays on your networking badge, match recommendations, and synchronizes to your main VJLINK profile. (Max 2MB)
                    </p>
                  </div>
                </div>
              </div>

              <div className="vp-field">
                <label>Your full name</label>
                <input 
                  value={name} 
                  onChange={e => setName(e.target.value)} 
                  placeholder="e.g. Sriram Charan" 
                  required 
                />
              </div>

              <div className="vp-field">
                <label>College / Institution</label>
                <input value="VNR VJIET" disabled style={{ background: '#f0f2eb', cursor: 'not-allowed' }} />
              </div>

              <div className="vp-field">
                <label>Branch / Department</label>
                <select value={department} onChange={e => setDepartment(e.target.value)}>
                  {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>

              <div className="vp-field">
                <label>Year of study</label>
                <select value={year} onChange={e => setYear(e.target.value)}>
                  {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>

              <div className="vp-field">
                <label>Roll number</label>
                <input 
                  value={rollNo} 
                  onChange={e => setRollNo(e.target.value.toUpperCase())} 
                  placeholder="e.g. 23071A66XX" 
                />
              </div>

              <div className="vp-field">
                <label>Section</label>
                <select value={section} onChange={e => setSection(e.target.value)}>
                  {SECTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div className="vp-field full">
                <label>WhatsApp / Mobile number <span style={{ color: 'var(--vp-muted)', fontWeight: 500 }}>(Private until connected)</span></label>
                <input 
                  type="tel" 
                  value={contact} 
                  onChange={e => setContact(e.target.value)} 
                  placeholder="e.g. 9876543210" 
                />
              </div>

              <div className="vp-field full">
                <label>A little about you</label>
                <textarea 
                  value={bio} 
                  onChange={e => setBio(e.target.value)} 
                  placeholder="What are you learning, building, or curious about? What problems excite you?" 
                />
              </div>

              <div className="vp-field">
                <label>School (Optional for alumni matches)</label>
                <AutocompleteInput 
                  type="school" 
                  value={schoolName} 
                  onChange={setSchoolName} 
                  placeholder="e.g. Hyderabad Public School" 
                />
              </div>

              <div className="vp-field">
                <label>Junior College (Optional)</label>
                <AutocompleteInput 
                  type="junior_college" 
                  value={juniorCollege} 
                  onChange={setJuniorCollege} 
                  placeholder="e.g. Narayana / Sri Chaitanya" 
                />
              </div>
            </div>
          )}

          {/* STEP 2: Skills & Passions */}
          {step === 2 && (
            <div>
              {/* Technical Focus Choice Group */}
              <div className="vp-field">
                <label>What technologies or domains excite you? Pick a few.</label>
                <div className="vp-choice-group" style={{ marginTop: '6px' }}>
                  {TECH_INTERESTS_LIST.map(topic => {
                    const isSelected = techInterests.includes(topic);
                    return (
                      <button 
                        key={topic} 
                        type="button" 
                        className={`vp-choice ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleItem(techInterests, setTechInterests, topic)}
                      >
                        {isSelected ? '✓ ' : '＋ '}{topic}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Skills Tags Input */}
              <div className="vp-field" style={{ marginTop: '22px' }}>
                <label>Skills & tools you know</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input 
                    value={skillInput} 
                    onChange={e => setSkillInput(e.target.value)} 
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddSkill(); } }}
                    placeholder="Type a skill and press Enter or Add (e.g. React, PyTorch, Figma)..." 
                  />
                  <button type="button" className="vp-btn vp-btn-dark vp-btn-small" onClick={() => handleAddSkill()}>
                    <Plus size={14} /> Add
                  </button>
                </div>

                {/* Selected Skills Chips */}
                {skills.length > 0 && (
                  <div className="vp-tag-row" style={{ marginTop: '10px' }}>
                    {skills.map(s => (
                      <span key={s} className="vp-skill-tag" style={{ background: '#eef0ff', color: 'var(--vp-ink)', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                        {s}
                        <X size={12} style={{ cursor: 'pointer' }} onClick={() => handleRemoveSkill(s)} />
                      </span>
                    ))}
                  </div>
                )}

                {/* Suggested Skills */}
                <div style={{ marginTop: '12px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--vp-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Quick suggestions:
                  </span>
                  <div className="vp-choice-group" style={{ marginTop: '6px' }}>
                    {SUGGESTED_SKILLS.filter(s => !skills.includes(s)).slice(0, 10).map(s => (
                      <button 
                        key={s} 
                        type="button" 
                        className="vp-choice" 
                        style={{ padding: '6px 10px', fontSize: '11px' }}
                        onClick={() => handleAddSkill(s)}
                      >
                        ＋ {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Outside Tech / Hobbies */}
              <div className="vp-field" style={{ marginTop: '22px' }}>
                <label>Outside the technical world</label>
                <div className="vp-choice-group" style={{ marginTop: '6px' }}>
                  {HOBBY_OPTIONS.map(hobby => {
                    const isSelected = hobbies.includes(hobby);
                    return (
                      <button 
                        key={hobby} 
                        type="button" 
                        className={`vp-choice ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleItem(hobbies, setHobbies, hobby)}
                      >
                        {isSelected ? '✓ ' : '＋ '}{hobby}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Intent & Collaboration */}
          {step === 3 && (
            <div>
              {/* Networking Intent */}
              <div className="vp-field">
                <label>What would you like to find at this event?</label>
                <div className="vp-choice-group" style={{ marginTop: '6px' }}>
                  {NETWORKING_INTENT_OPTIONS.map(intent => {
                    const isSelected = networkingIntent.includes(intent);
                    return (
                      <button 
                        key={intent} 
                        type="button" 
                        className={`vp-choice ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleItem(networkingIntent, setNetworkingIntent, intent)}
                      >
                        {isSelected ? '✓ ' : '＋ '}{intent}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Skills looking for in teammates */}
              <div className="vp-field" style={{ marginTop: '20px' }}>
                <label>Skills you're looking for in teammates / collaborators</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input 
                    value={lookingForInput} 
                    onChange={e => setLookingForInput(e.target.value)} 
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddLookingForSkill(); } }}
                    placeholder="e.g. UI/UX Designer, Backend Dev, ML specialist..." 
                  />
                  <button type="button" className="vp-btn vp-btn-dark vp-btn-small" onClick={() => handleAddLookingForSkill()}>
                    <Plus size={14} /> Add
                  </button>
                </div>
                {lookingForSkills.length > 0 && (
                  <div className="vp-tag-row" style={{ marginTop: '10px' }}>
                    {lookingForSkills.map(s => (
                      <span key={s} className="vp-skill-tag" style={{ background: '#eff0ec', color: 'var(--vp-ink)', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                        {s}
                        <X size={12} style={{ cursor: 'pointer' }} onClick={() => handleRemoveLookingForSkill(s)} />
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Event Activities Participation */}
              {localActivities.length > 0 && (
                <div className="vp-field" style={{ marginTop: '20px' }}>
                  <label>Which event activities are you joining?</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
                    {localActivities.map(act => {
                      const isSelected = selectedActivities.includes(act.id);
                      return (
                        <div 
                          key={act.id} 
                          onClick={() => toggleItem(selectedActivities, setSelectedActivities, act.id)}
                          style={{
                            padding: '10px 14px',
                            borderRadius: '12px',
                            border: `1px solid ${isSelected ? 'var(--vp-ink)' : 'var(--vp-line)'}`,
                            background: isSelected ? 'var(--vp-ink)' : 'var(--vp-paper)',
                            color: isSelected ? '#fff' : 'var(--vp-ink)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: 'pointer',
                            transition: 'all .18s'
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '13px' }}>{act.name}</div>
                            {act.category && (
                              <div style={{ fontSize: '11px', opacity: 0.75 }}>{act.category}</div>
                            )}
                          </div>
                          <span style={{ fontSize: '12px', fontWeight: 800 }}>
                            {isSelected ? '✓ Selected' : '＋ Join'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Social profiles */}
              <div className="vp-form-grid" style={{ marginTop: '20px' }}>
                <div className="vp-field">
                  <label><Github size={13} style={{ display: 'inline', marginRight: 4 }} /> GitHub Username</label>
                  <input 
                    value={github} 
                    onChange={e => setGithub(e.target.value.replace(/https?:\/\/github\.com\//, ''))} 
                    placeholder="e.g. sriramcharan" 
                  />
                </div>
                <div className="vp-field">
                  <label><Linkedin size={13} style={{ display: 'inline', marginRight: 4 }} /> LinkedIn Username or URL</label>
                  <input 
                    value={linkedin} 
                    onChange={e => setLinkedin(e.target.value)} 
                    placeholder="e.g. sriram-charan" 
                  />
                </div>
              </div>

              {/* Privacy Guarantee Panel */}
              <div style={{ 
                marginTop: '22px', 
                background: '#f5f8e9', 
                border: '1px solid #e3eccd', 
                borderRadius: '14px', 
                padding: '14px 16px' 
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '7px', fontWeight: 800, fontSize: '13px', color: '#273414' }}>
                  <Lock size={15} /> Your profile, your choice.
                </div>
                <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#485637', lineHeight: 1.5 }}>
                  Your phone number and private contact details remain protected and are never exposed publicly. They become visible only when both you and another attendee mutually accept a connection request.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Modal Foot */}
        <div className="vp-modal-foot">
          {step > 1 ? (
            <button 
              type="button" 
              className="vp-btn vp-btn-outline" 
              onClick={() => setStep(s => s - 1)}
            >
              ← Back
            </button>
          ) : (
            <button 
              type="button" 
              className="vp-btn vp-btn-soft" 
              onClick={onClose}
            >
              Skip for now
            </button>
          )}

          <div style={{ display: 'flex', gap: '8px' }}>
            {step < 3 ? (
              <button 
                type="button" 
                className="vp-btn vp-btn-dark" 
                onClick={() => setStep(s => s + 1)}
              >
                Continue →
              </button>
            ) : (
              <button 
                type="button" 
                className="vp-btn vp-btn-lime" 
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader size={14} className="vp-spin" /> Saving profile...
                  </>
                ) : (
                  'Save my profile ✓'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
